/**
 * RegisterPage — Inscription d'un administré.
 *
 * Refonte R5 (mai 2026) :
 *   - LogoMark UserPlus + Card glass
 *   - Select tenants en select natif stylé (cohérence Input glass)
 *   - Checkboxes CGU/PC dans un sous-bloc glass discret
 *   - Écran de confirmation après succès avec LogoMark Mail
 *
 * Logique métier 100% préservée :
 *   - Fetch tenants public + legal versions en parallèle (useEffect)
 *   - Pré-sélection si une seule commune dispo
 *   - Validation tenantId + CGU + PC + legalVersions chargées
 *   - registerAdministre() avec versions CGU/PC
 *   - Après succès : écran "vérifie ton mail" (pas d'auth auto)
 */

import { useState, useEffect, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Field, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { PublicLayout } from '@/components/PublicLayout';
import { useAuth } from '@/lib/auth-context';
import { api, ApiException } from '@/lib/api';
import { Mail, Loader2, UserPlus, AlertCircle } from 'lucide-react';
import type {
  TenantPublic,
  LegalVersionsResponse,
} from '@roronoa-collectivites/shared-types';

export function RegisterPage() {
  const { registerAdministre } = useAuth();

  const [codePostal, setCodePostal] = useState('');
  const [tenants, setTenants] = useState<TenantPublic[] | null>(null);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantsError, setTenantsError] = useState<string | null>(null);
  const [legalVersions, setLegalVersions] = useState<LegalVersionsResponse | null>(null);

  const [form, setForm] = useState({
    email: '',
    password: '',
    prenom: '',
    nom: '',
    telephone: '',
    tenantId: '',
    cguAccepted: false,
    pcAccepted: false,
  });

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  // Au montage : charger les versions des documents légaux (CGU / politique de conf.)
  useEffect(() => {
    let cancelled = false;
    api.legal
      .versions()
      .then((v) => {
        if (!cancelled) setLegalVersions(v);
      })
      .catch(() => {
        /* le garde-fou du submit gère l'absence de versions légales */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Dès qu'un code postal valide (5 chiffres) est saisi, charger les communes
  // correspondantes. Tant que le code postal est incomplet : pas de liste.
  useEffect(() => {
    if (!/^\d{5}$/.test(codePostal)) {
      setTenants(null);
      setTenantsError(null);
      setForm((f) => (f.tenantId ? { ...f, tenantId: '' } : f));
      return;
    }
    let cancelled = false;
    setTenantsLoading(true);
    setTenantsError(null);
    api.tenants
      .public(codePostal)
      .then((resp) => {
        if (cancelled) return;
        setTenants(resp.items);
        // Pré-sélection si une seule commune correspond au code postal
        setForm((f) => ({
          ...f,
          tenantId: resp.items.length === 1 ? resp.items[0]!.id : '',
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        setTenants([]);
        setForm((f) => (f.tenantId ? { ...f, tenantId: '' } : f));
        setTenantsError(
          err instanceof ApiException
            ? err.payload.error
            : 'Impossible de charger les communes. Réessayez dans un instant.'
        );
      })
      .finally(() => {
        if (!cancelled) setTenantsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [codePostal]);

  function update<K extends keyof typeof form>(key: K, value: typeof form[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.tenantId) {
      setError('Veuillez sélectionner votre commune.');
      return;
    }
    if (!form.cguAccepted) {
      setError('Vous devez accepter les CGU pour créer un compte.');
      return;
    }
    if (!form.pcAccepted) {
      setError('Vous devez accepter la politique de confidentialité pour créer un compte.');
      return;
    }
    if (!legalVersions) {
      setError('Configuration en cours de chargement, veuillez réessayer dans un instant.');
      return;
    }

    setLoading(true);
    try {
      const result = await registerAdministre({
        email: form.email,
        password: form.password,
        prenom: form.prenom,
        nom: form.nom,
        telephone: form.telephone || undefined,
        tenantId: form.tenantId,
        cguAccepted: true as const,
        cguVersion: legalVersions.cgu,
        pcAccepted: true as const,
        pcVersion: legalVersions.politiqueConfidentialite,
      });
      setSubmittedEmail(result.email);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.payload.error);
      } else {
        setError('Une erreur est survenue, veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  }

  // ---------- ÉCRAN DE CONFIRMATION ----------
  if (submittedEmail) {
    return (
      <PublicLayout maxWidth="sm">
        <div className="text-center space-y-5">
          <div className="flex justify-center">
            <LogoMark icon={Mail} accent="tech" size="lg" aria-label="Email envoyé" />
          </div>
          <div>
            <SectionEyebrow as="div">Inscription presque terminée</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
              Votre compte est presque prêt
            </h1>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed">
            Un email de vérification vient d'être envoyé à{' '}
            <strong className="text-ink">{submittedEmail}</strong>.
            <br />
            Cliquez sur le lien qu'il contient pour activer votre compte. Il est valable 24 heures.
          </p>
          <p className="text-xs text-ink-subtle leading-relaxed">
            Pensez à vérifier vos courriers indésirables. Vous pouvez fermer cette page en
            attendant.
          </p>
          <div className="pt-2">
            <Link to="/connexion">
              <Button variant="secondary">Retour à la connexion</Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // ---------- FORMULAIRE ----------
  return (
    <PublicLayout maxWidth="md">
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex justify-center">
            <LogoMark icon={UserPlus} accent="tech" size="lg" aria-label="Créer un compte" />
          </div>
          <SectionEyebrow as="div" className="mt-4">Espace administré</SectionEyebrow>
          <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
            Créer un compte
          </h1>
          <p className="mt-2 text-sm text-ink-muted">
            Rejoignez l'espace numérique de votre commune.
          </p>
        </div>

        <Card variant="glass" padded>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <Alert variant="error" icon={AlertCircle}>
                {error}
              </Alert>
            )}
            {tenantsError && (
              <Alert variant="warning" icon={AlertCircle}>
                {tenantsError}
              </Alert>
            )}

            {/* Étape 1 — Code postal */}
            <Field
              label="Code postal de votre commune"
              htmlFor="codePostal"
              hint="Saisissez votre code postal pour trouver votre commune."
              required
            >
              <Input
                id="codePostal"
                inputMode="numeric"
                autoComplete="postal-code"
                placeholder="17780"
                value={codePostal}
                onChange={(e) => setCodePostal(e.target.value.replace(/\D/g, '').slice(0, 5))}
                maxLength={5}
                required
                className="h-12 sm:h-11"
              />
            </Field>

            {/* Étape 2 — Commune (dépend du code postal saisi) */}
            <Field
              label="Votre commune"
              htmlFor="tenantId"
              hint="Sélectionnez la commune où vous résidez."
              required
            >
              {codePostal.length < 5 ? (
                <div className="flex h-12 items-center rounded-r-lg border border-dashed border-border bg-surface/30 backdrop-blur-glass px-3.5 text-sm text-ink-subtle sm:h-11">
                  Saisissez d'abord votre code postal.
                </div>
              ) : tenantsLoading ? (
                <div className="flex h-12 items-center gap-2 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3.5 text-sm text-ink-muted sm:h-11">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Recherche des communes…
                </div>
              ) : tenants && tenants.length === 0 ? (
                <div className="rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass px-3.5 py-2.5 text-sm text-ink-muted leading-relaxed">
                  Aucune commune n'est inscrite pour le code postal{' '}
                  <strong className="text-ink">{codePostal}</strong>. Vérifiez le code,
                  ou rapprochez-vous de votre mairie.
                </div>
              ) : (
                <select
                  id="tenantId"
                  value={form.tenantId}
                  onChange={(e) => update('tenantId', e.target.value)}
                  required
                  className="h-12 sm:h-11 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3.5 text-sm text-ink shadow-soft transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg focus:border-tech disabled:cursor-not-allowed disabled:bg-surface-hi/40 hover:border-border-strong"
                >
                  <option value="" disabled>
                    — Sélectionnez votre commune —
                  </option>
                  {(tenants ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nom}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            {/* Identité */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Prénom" htmlFor="prenom" required>
                <Input
                  id="prenom"
                  autoComplete="given-name"
                  value={form.prenom}
                  onChange={(e) => update('prenom', e.target.value)}
                  required
                  className="h-12 sm:h-11"
                />
              </Field>
              <Field label="Nom" htmlFor="nom" required>
                <Input
                  id="nom"
                  autoComplete="family-name"
                  value={form.nom}
                  onChange={(e) => update('nom', e.target.value)}
                  required
                  className="h-12 sm:h-11"
                />
              </Field>
            </div>

            {/* Email */}
            <Field label="Adresse email" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="vous@exemple.fr"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                required
                className="h-12 sm:h-11"
              />
            </Field>

            {/* Téléphone optionnel */}
            <Field
              label="Téléphone"
              htmlFor="telephone"
              hint="Optionnel — utile si la mairie souhaite vous rappeler à propos d'un signalement."
            >
              <Input
                id="telephone"
                type="tel"
                autoComplete="tel"
                placeholder="06 12 34 56 78"
                value={form.telephone}
                onChange={(e) => update('telephone', e.target.value)}
                className="h-12 sm:h-11"
              />
            </Field>

            {/* Mot de passe */}
            <Field
              label="Mot de passe"
              htmlFor="password"
              hint="Au moins 12 caractères. Évitez les mots du dictionnaire et vos informations personnelles."
              required
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                minLength={12}
                required
                className="h-12 sm:h-11"
              />
            </Field>

            {/* Acceptations RGPD — bloc glass discret */}
            <div className="space-y-3 rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass p-3.5">
              <label className="flex items-start gap-3 cursor-pointer text-sm text-ink-muted leading-relaxed">
                <input
                  type="checkbox"
                  checked={form.cguAccepted}
                  onChange={(e) => update('cguAccepted', e.target.checked)}
                  required
                  className="mt-0.5 h-4 w-4 rounded border-border bg-surface text-tech focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg cursor-pointer"
                />
                <span>
                  J'ai lu et j'accepte les{' '}
                  <Link
                    to="/cgu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-tech-fg hover:underline"
                  >
                    Conditions générales d'utilisation
                  </Link>
                  .{' '}
                  <span className="text-urgent" aria-label="requis">
                    *
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer text-sm text-ink-muted leading-relaxed">
                <input
                  type="checkbox"
                  checked={form.pcAccepted}
                  onChange={(e) => update('pcAccepted', e.target.checked)}
                  required
                  className="mt-0.5 h-4 w-4 rounded border-border bg-surface text-tech focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg cursor-pointer"
                />
                <span>
                  J'ai lu et j'accepte la{' '}
                  <Link
                    to="/politique-confidentialite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-tech-fg hover:underline"
                  >
                    Politique de confidentialité
                  </Link>
                  .{' '}
                  <span className="text-urgent" aria-label="requis">
                    *
                  </span>
                </span>
              </label>
            </div>

            <Button type="submit" loading={loading} className="w-full" size="lg">
              Créer mon compte
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-ink-muted">
          Déjà un compte&nbsp;?{' '}
          <Link
            to="/connexion"
            className="font-medium text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md px-1"
          >
            Se connecter
          </Link>
        </p>
      </div>
    </PublicLayout>
  );
}
